import type { Direction } from '@angular/cdk/bidi';
import type { ViewContainerRef, Type } from '@angular/core';

/** Valid ARIA roles for a modal element. */
export type ModalRole = 'dialog' | 'alertdialog';
export type ModalSize = 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large' | 'full-screen';

/**
 * Possible overrides for CDK Overlay's position.
 * If no parameters are provided, it's centered vertically and horizontally within the viewport using flexbox.
 *
 * CDK Overlay is a parent of Modal Container:
 * CDK Overlay container -> CDK Overlay -> Modal Container (ModalWindowComponent) -> content component/template
 */
export interface ModalPosition {
  /** Override for the modal's top position. */
  top?: string;

  /** Override for the modal's bottom position. */
  bottom?: string;

  /** Override for the modal's left position. */
  left?: string;

  /** Override for the modal's right position. */
  right?: string;
}

export const DEFAULT_POSITION: ModalPosition = {
  top: '10px'
};

/**
 * Configuration options available when opening new modal windows.*
 */
// TODO: cleanup unused options
export class ModalOptions {
  component?: Type<unknown>;
  /**
   * Where the attached component should live in Angular's *logical* component tree. This affects
   * what is available for injection and the change detection order for the component instantiated inside of the modal.
   * This does not affect where the modal content will be rendered.
   */
  viewContainerRef?: ViewContainerRef;

  /*****************************************************
   *    CDK Overlay configuration options
   *****************************************************/

  /** Whether the modal has a backdrop. */
  hasBackdrop?: boolean = true;

  /** Custom class(es) for the backdrop. */
  backdropClass?: string | undefined = 'td-wb-modal-backdrop';

  /** Direction of the text in the overlay panel. Defaulting it to left-to-right. */
  direction?: Direction = 'ltr';

  /*****************************************************
   *    Modal Container configuration options
   *****************************************************/

  /** ID for the modal. If omitted, a unique one will be generated. */
  id?: string;

  /** The ARIA role of the modal element. */
  role?: ModalRole = 'dialog';

  /** Position overrides for the modal. */
  position?: ModalPosition = DEFAULT_POSITION;

  /** Size of a new modal window. */
  size?: ModalSize = 'medium';

  /** The width of the modal. */
  width?: string = '';

  /** The height of the modal. */
  height?: string = '';

  /** aria-describedby to assign to modal element. */
  ariaDescribedBy?: string | undefined = undefined;

  /** aria-label to assign to the modal element */
  ariaLabel?: string | undefined = 'tdWb.modalHeader.TITLE';

  /** aria-labelledby to assign to the modal element */
  ariaLabelledBy?: string | undefined = undefined;

  /** Whether the modal should focus the first focusable element on open. */
  autoFocus?: boolean = true;

  /**
   * Whether to close the modal on backdrop click (false by default).
   * If true, modal will be closed with a reason 'backdropClick'.
   */
  closeOnBackDropClick?: boolean = false;

  /**
   * Whether to close the modal when escape Key is pressed (true by default).
   * If true, modal will be closed with a reason 'escapeKey'.
   */
  closeOnEscapeKey?: boolean = true;

  closeAsync?: boolean = false;
}
